import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const BusinessListPanel = ({ businesses, selectedBusiness, onBusinessSelect, userLocation }) => {
  const navigate = useNavigate();

  const calculateDistance = (businessLat, businessLng) => {
    if (!userLocation) return null;
    const R = 6371;
    const dLat = (businessLat - userLocation?.lat) * Math.PI / 180;
    const dLng = (businessLng - userLocation?.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(userLocation?.lat * Math.PI / 180) * Math.cos(businessLat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c)?.toFixed(1);
  };

  const handleCardClick = (business) => {
    onBusinessSelect(business);
  };

  const handleViewDetails = (e, businessId) => {
    e?.stopPropagation();
    navigate(`/store-details?id=${businessId}`);
  };

  if (businesses?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 py-12">
        <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-4">
          <Icon name="MapPin" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
          Nessuna attività trovata
        </h3>
        <p className="font-body text-sm text-muted-foreground text-center max-w-xs">
          Prova a modificare i filtri o a esplorare un'altra area della mappa
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-4 py-4 space-y-4">
      {businesses?.map((business) => {
        const distance = calculateDistance(business?.lat, business?.lng);
        const isSelected = selectedBusiness?.id === business?.id;

        return (
          <div
            key={business?.id}
            onClick={() => handleCardClick(business)}
            className={`bg-card rounded-lg shadow-warm overflow-hidden transition-smooth cursor-pointer ${
              isSelected ? 'ring-3 ring-primary' : 'hover:shadow-warm-md'
            }`}
          >
            <div className="relative h-40 md:h-48 overflow-hidden">
              <Image
                src={business?.image}
                alt={business?.imageAlt}
                className="w-full h-full object-cover"
              />
              {business?.badge && (
                <div className="absolute top-3 left-3 bg-accent px-3 py-1 rounded-full">
                  <span className="font-caption text-xs font-semibold text-accent-foreground">
                    {business?.badge}
                  </span>
                </div>
              )}
              {distance && (
                <div className="absolute top-3 right-3 bg-card/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1">
                  <Icon name="MapPin" size={14} color="var(--color-primary)" />
                  <span className="font-caption text-xs font-semibold text-foreground">
                    {distance} km
                  </span>
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-1 truncate">
                    {business?.name}
                  </h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-caption text-sm text-muted-foreground">
                      {business?.category}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="font-caption text-sm text-muted-foreground">
                      {business?.zone}
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Image
                    src={business?.logo}
                    alt={business?.logoAlt}
                    className="w-8 h-8 object-contain"
                  />
                </div>
              </div>

              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)]?.map((_, index) => (
                  <Icon
                    key={index}
                    name={index < Math.floor(business?.rating) ? "Star" : "Star"}
                    size={16}
                    color={index < Math.floor(business?.rating) ? "var(--color-primary)" : "var(--color-muted)"}
                    className={index < Math.floor(business?.rating) ? "fill-primary" : "fill-muted"}
                  />
                ))}
                <span className="font-caption text-sm text-muted-foreground ml-2">
                  {business?.rating} ({business?.reviewCount})
                </span>
              </div>

              {business?.address && (
                <div className="flex items-start gap-2 mb-3">
                  <Icon name="MapPin" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="font-body text-sm text-muted-foreground line-clamp-2">
                    {business?.address}
                  </p>
                </div>
              )}

              {business?.promotion && (
                <div className="bg-accent/10 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="Tag" size={16} color="var(--color-accent)" />
                    <span className="font-caption text-xs font-semibold text-accent uppercase">
                      Promo Attiva
                    </span>
                  </div>
                  <p className="font-body text-sm text-foreground line-clamp-2">
                    {business?.promotion}
                  </p>
                </div>
              )}

              <Button
                variant="default"
                size="sm"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={(e) => handleViewDetails(e, business?.id)}
              >
                Vedi Dettagli
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BusinessListPanel;